// Step 2 do wizard: lista de entitlements com add/remove.
// Valida count×type localmente antes de avançar.

import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { CreatePlanInput } from '@control-center/shared';

import { Field, Select } from '../../../components/clients/form-primitives';
import { PillButton } from '../../../components/clients/PillButton';
import { EntitlementsPreview } from '../../../components/plans/EntitlementsPreview';
import { LimitsOverrideField } from '../../../components/plans/LimitsOverrideField';
import type { ExternalPlanEntity } from '../../../lib/external-plans-api';
import type { SaasProductEntity } from '../../../lib/saas-products-api';

interface Step2EntitlementsProps {
  data: CreatePlanInput;
  saasProducts: SaasProductEntity[];
  externalPlans: ExternalPlanEntity[];
  onPatch: (patch: Partial<CreatePlanInput>) => void;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
}

export function Step2Entitlements({
  data,
  saasProducts,
  externalPlans,
  onPatch,
  onBack,
  onNext,
  onCancel,
}: Step2EntitlementsProps): JSX.Element {
  const entitlements = data.entitlements ?? [];

  const externalsBySaas = useMemo(() => {
    const map = new Map<string, ExternalPlanEntity[]>();
    for (const ext of externalPlans) {
      if (ext.status === 'descontinuado') continue;
      const list = map.get(ext.saasProductId) ?? [];
      list.push(ext);
      map.set(ext.saasProductId, list);
    }
    return map;
  }, [externalPlans]);

  const { required, label } = useMemo(() => {
    switch (data.type) {
      case 'service_only':
        return { required: 'zero', label: 'service_only deve ter 0 entitlements.' };
      case 'single_saas':
        return { required: 'one', label: 'single_saas deve ter exatamente 1 entitlement.' };
      default:
        return { required: 'two_or_more', label: 'bundle deve ter 2+ entitlements.' };
    }
  }, [data.type]);

  const n = entitlements.length;
  const validCount =
    (required === 'zero' && n === 0) ||
    (required === 'one' && n === 1) ||
    (required === 'two_or_more' && n >= 2);

  const usedSaas = new Set(entitlements.map((e) => e.saas_product_id));
  const duplicates = entitlements
    .filter((e, i) => entitlements.findIndex((x) => x.saas_product_id === e.saas_product_id) !== i)
    .map((e) => e.saas_product_id);

  const allFilled = entitlements.every(
    (e) => Boolean(e.saas_product_id) && Boolean(e.external_plan_id),
  );

  const [invalidJsonIdx, setInvalidJsonIdx] = useState<Set<number>>(() => new Set());
  const hasInvalidJson = invalidJsonIdx.size > 0;

  const canAdvance = validCount && duplicates.length === 0 && allFilled && !hasInvalidJson;

  // Story 7.1 AC3 — drag&drop nativo (sem dep externa).
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  function handleDragStart(index: number) {
    return (event: React.DragEvent<HTMLDivElement>) => {
      setDragIndex(index);
      event.dataTransfer.effectAllowed = 'move';
    };
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(targetIndex: number) {
    return (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (dragIndex === null || dragIndex === targetIndex) {
        setDragIndex(null);
        return;
      }
      const reordered = [...entitlements];
      const [moved] = reordered.splice(dragIndex, 1);
      if (moved) reordered.splice(targetIndex, 0, moved);
      onPatch({
        entitlements: reordered.map((e, i) => ({ ...e, display_order: i })),
      });
      setDragIndex(null);
    };
  }

  function add(): void {
    onPatch({
      entitlements: [
        ...entitlements,
        { saas_product_id: '', external_plan_id: '', limits_override: {}, display_order: n },
      ],
    });
  }

  function remove(index: number): void {
    onPatch({ entitlements: entitlements.filter((_, i) => i !== index) });
    setInvalidJsonIdx((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  }

  function markJsonValidity(index: number, isValid: boolean): void {
    setInvalidJsonIdx((prev) => {
      const has = prev.has(index);
      if (isValid && !has) return prev;
      if (!isValid && has) return prev;
      const next = new Set(prev);
      if (isValid) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function patchAt(index: number, patch: Partial<(typeof entitlements)[number]>): void {
    onPatch({
      entitlements: entitlements.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    });
  }

  return (
    <div className="space-y-5">
      <div className="rounded-[10px] border border-line bg-panel-2 p-3 text-[13px]">
        <strong>Regra do tipo:</strong> {label}
        <div className="mt-1 text-[12px] text-muted">
          Atual: {n} entitlement{n === 1 ? '' : 's'}
          {validCount ? ' ✓' : ' — ajuste para avançar.'}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-3">
          {entitlements.length === 0 ? (
            <div className="rounded-[10px] border border-dashed border-line bg-panel-3/30 p-6 text-center text-[12px] text-muted">
              {required === 'zero'
                ? 'Sem entitlements (service_only).'
                : 'Adicione o(s) SaaS Product(s) deste plano.'}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {entitlements.map((ent, idx) => {
                const options = ent.saas_product_id
                  ? (externalsBySaas.get(ent.saas_product_id) ?? [])
                  : [];
                const isDup = duplicates.includes(ent.saas_product_id);
                const isDragging = dragIndex === idx;
                return (
                  <div
                    key={idx}
                    draggable={entitlements.length > 1}
                    onDragStart={handleDragStart(idx)}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop(idx)}
                    onDragEnd={() => setDragIndex(null)}
                    className={`rounded-[10px] border bg-panel-2 p-3 transition-opacity ${isDup ? 'border-bad/60' : 'border-line'} ${isDragging ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      {entitlements.length > 1 && (
                        <button
                          type="button"
                          aria-label="Arrastar para reordenar"
                          className="mt-6 flex h-8 w-5 shrink-0 cursor-grab items-center justify-center text-muted-3 hover:text-ink active:cursor-grabbing"
                        >
                          <GripVertical size={14} strokeWidth={1.8} />
                        </button>
                      )}
                      <div className="flex-1 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <Field
                          label={`SaaS #${idx + 1}`}
                          required
                          error={isDup ? 'Duplicado: mesmo SaaS já está no plano.' : undefined}
                        >
                          {({ inputId }) => (
                            <Select
                              id={inputId}
                              value={ent.saas_product_id}
                              onChange={(e) =>
                                patchAt(idx, {
                                  saas_product_id: e.target.value,
                                  external_plan_id: '',
                                })
                              }
                            >
                              <option value="" disabled>
                                Selecione o SaaS…
                              </option>
                              {saasProducts
                                .filter(
                                  (s) =>
                                    s.status === 'ativo' ||
                                    s.status === 'em_desenvolvimento' ||
                                    s.id === ent.saas_product_id,
                                )
                                .map((s) => {
                                  const inUseElsewhere =
                                    usedSaas.has(s.id) && s.id !== ent.saas_product_id;
                                  return (
                                    <option key={s.id} value={s.id} disabled={inUseElsewhere}>
                                      {s.name}
                                      {inUseElsewhere ? ' (já em uso)' : ''}
                                    </option>
                                  );
                                })}
                            </Select>
                          )}
                        </Field>
                        <Field
                          label="Plano externo"
                          required
                          hint={!ent.saas_product_id ? 'Escolha o SaaS primeiro.' : undefined}
                        >
                          {({ inputId }) => (
                            <Select
                              id={inputId}
                              value={ent.external_plan_id}
                              onChange={(e) => patchAt(idx, { external_plan_id: e.target.value })}
                              disabled={!ent.saas_product_id || options.length === 0}
                            >
                              <option value="" disabled>
                                {options.length === 0 ? 'Nenhum plano disponível' : 'Selecione…'}
                              </option>
                              {options.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name} ({p.slug})
                                </option>
                              ))}
                            </Select>
                          )}
                        </Field>
                      </div>
                      <button
                        type="button"
                        aria-label={`Remover SaaS #${idx + 1}`}
                        onClick={() => remove(idx)}
                        className="mt-6 flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad"
                      >
                        <Trash2 size={14} strokeWidth={1.8} />
                      </button>
                    </div>
                    <div className="mt-3">
                      <LimitsOverrideField
                        value={(ent.limits_override as Record<string, unknown> | undefined) ?? {}}
                        onChange={(next, isValid) => {
                          if (isValid) patchAt(idx, { limits_override: next });
                          markJsonValidity(idx, isValid);
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {required !== 'zero' && (
            <PillButton icon={<Plus size={12} strokeWidth={2} />} type="button" onClick={add}>
              Adicionar SaaS
            </PillButton>
          )}
        </div>
        <EntitlementsPreview
          entitlements={entitlements}
          saasProducts={saasProducts}
          externalPlans={externalPlans}
        />
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onBack}>
          ← Voltar
        </PillButton>
        <div className="flex items-center gap-2">
          {hasInvalidJson && (
            <span className="text-[11px] text-red-300/80">Corrija o JSON de override.</span>
          )}
          <PillButton type="button" onClick={onCancel}>
            Cancelar
          </PillButton>
          <PillButton solid type="button" onClick={onNext} disabled={!canAdvance}>
            Avançar →
          </PillButton>
        </div>
      </div>
    </div>
  );
}
